import React, { useEffect, useCallback, useState } from "react";
import { Box } from "@mui/material";
import SuggestionTable, { COLUMNS_CONSTANTS as SUGGESTION_COLUMNS } from "./suggestion/SuggestionTable";
import CreateSuggestionModal from "./suggestion/CreateSuggestionModal";
import sh from "bundles/common/utils/sh";
import { useSnackbar } from "notistack";
import {
  EMPTY_SUGGESTION_FILTERS,
} from "bundles/Internal/components/suggestion/SuggestionTableFilters";

function buildSearchFilters(activeFilters) {
  const filters = {};
  let hasAny = false;

  if (activeFilters.status?.length) {
    filters.status = activeFilters.status;
    hasAny = true;
  }

  if (activeFilters.selected_by?.length) {
    filters.selected_by = activeFilters.selected_by;
    hasAny = true;
  }

  return hasAny ? filters : null;
}

const SuggestionsIndexPage = () => {
  const [suggestions, setSuggestions] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState(EMPTY_SUGGESTION_FILTERS);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchSuggestions = useCallback((query = "", pageNum = 1, limit = 25, activeFilters = {}) => {
    const hasQuery = Boolean(String(query || "").trim());
    const searchFilters = buildSearchFilters(activeFilters);
    const hasFilters = searchFilters !== null;

    setLoading(true);
    sh.get("/internal/search/suggestions", {
      params: {
        q: hasQuery ? query : undefined,
        page: pageNum,
        limit,
        ...(hasFilters && { filters: JSON.stringify(searchFilters) }),
      },
    })
      .then((res) => {
        const payload = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setSuggestions(payload);
        if (res.data?.pagy) {
          setTotalCount(res.data.pagy.count || payload.length);
        } else {
          setTotalCount(payload.length);
        }
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || String(err?.message || "").toLowerCase().includes("canceled")) return;
        enqueueSnackbar("Failed to fetch suggestions", { variant: "error" });
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchSuggestions(searchQuery, 1, rowsPerPage, filters);
    }, 250);
    return () => clearTimeout(t);
  }, [fetchSuggestions, searchQuery, rowsPerPage, filters]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    fetchSuggestions(searchQuery, newPage, rowsPerPage, filters);
  }, [fetchSuggestions, searchQuery, rowsPerPage, filters]);

  const handleRowsPerPageChange = useCallback((newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  const handleCreateSuggestion = useCallback((payload, { reset }) => {
    setSubmitting(true);
    sh.post("/internal/suggestions", { suggestion: payload })
      .then((res) => {
        enqueueSnackbar("Suggestion created", { variant: "success" });
        reset();
        setCreateModalOpen(false);
        if (res.data) {
          setSuggestions((prev) => [res.data, ...prev]);
          setTotalCount((prev) => prev + 1);
        }
      })
      .catch((err) => {
        const messages = err?.response?.data?.errors;
        enqueueSnackbar(
          messages ? messages.join(", ") : "Failed to create suggestion",
          { variant: "error" }
        );
      })
      .finally(() => setSubmitting(false));
  }, [enqueueSnackbar]);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(180deg, #F9FAFF 0%, #EEF0F6 100%)",
      }}
    >
      <SuggestionTable
        suggestions={suggestions}
        onQueryChange={(q) => setSearchQuery(q)}
        filters={filters}
        onFiltersChange={setFilters}
        onAddClick={() => setCreateModalOpen(true)}
        page={page}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={loading}
      />
      <CreateSuggestionModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateSuggestion}
        submitting={submitting}
      />
    </Box>
  );
};

export default SuggestionsIndexPage;
